import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useWidth } from '../../../hooks/useWidth';
import { TimeRangeRow } from './WorkSchedulePanel';
import type { TimeRange } from './WorkSchedulePanel';
import { timeToMinutes, validateRange } from './workScheduleUtils';
import CircularProgress from '../../common/CircularProgress';
import { CalendarRecurrentIcon } from './icons';
import { formatCalendarDay } from './utils';

interface WorkSchedule {
  id: string | number;
  psychologistId: number | string;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
}

function toMondayIdx(jsDay: number): number {
  return (jsDay + 6) % 7;
}

// Stable empty array — avoids creating a new reference on every render
const EMPTY_INTERVALS: Array<{ startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }> = [];

interface WeekSummaryDay {
  date: string;
  weekdayIdx: number;
  ranges: TimeRange[];
}

interface SpecificDayPanelProps {
  initialDate: string;
  existingSchedules: WorkSchedule[];
  dateSpecificSlots?: Array<{ date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>;
  /** Días de la semana visible que ya tienen "Día específico" configurado, agrupados por fecha. */
  weekSummary?: WeekSummaryDay[];
  onSave: (inputs: Array<{ startTime: string; endTime: string; weekdayIdx: number; date: string; scheduleType: 'standard' | 'reschedule_only' }>, date: string) => Promise<void>;
  onClose: () => void;
  onPreviewChange?: (slots: Array<{ date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>) => void;
  fetchSlotsForWeek?: (weekStart: Date) => Promise<Array<{ date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>>;
  loading?: boolean;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onDateChange?: (date: string) => void;
  mockRescheduleEntry?: boolean;
  blockedSlots?: Array<{ date: string; time: string; duration?: number }>;
  /** Called when the user clicks the "Cómo funciona tu agenda" help link. */
  onOpenAgendaHelp?: () => void;
}

const SpecificDayPanel: React.FC<SpecificDayPanelProps> = ({
  initialDate,
  existingSchedules,
  dateSpecificSlots = [],
  weekSummary = [],
  onSave,
  onClose,
  onPreviewChange,
  fetchSlotsForWeek,
  loading = false,
  onHasChangesChange,
  onDateChange,
  mockRescheduleEntry = false,
  blockedSlots = [],
  onOpenAgendaHelp,
}) => {
  const { size } = useWidth();
  const isMobile = size === 's';
  const [vpWidth, setVpWidth] = useState(() => window.visualViewport?.width ?? window.innerWidth);
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVpWidth(vv.width);
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [isMobile]);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [saving, setSaving] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [showBlockedConfirm, setShowBlockedConfirm] = useState(false);
  const [ranges, setRanges] = useState<TimeRange[]>([]);
  const [initialRanges, setInitialRanges] = useState<TimeRange[]>([]);
  const dateInputRef = useRef<HTMLInputElement>(null);
  // Track which date we've already initialized for — prevents re-init on parent re-renders
  const initializedDateRef = useRef<string | null>(null);
  // Always holds the latest slotsForDate so the timer reads fresh data
  const slotsForDateRef = useRef<Array<{ startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>>([]);
  // True when the initial fill found no data — triggers late-arriving data effect
  const initialFillEmptyRef = useRef(false);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const weekdayIdx = useMemo(() => {
    if (!selectedDate) return -1;
    const [y, m, d] = selectedDate.split('-').map(Number);
    return toMondayIdx(new Date(y, (m || 1) - 1, d || 1).getDay());
  }, [selectedDate]);

  const schedulesForDay = useMemo(
    () => (selectedDate ? existingSchedules.filter(s => s.weekday === weekdayIdx && s.active) : []),
    [existingSchedules, weekdayIdx, selectedDate],
  );

  const slotsForDate = useMemo(() => {
    if (!selectedDate) return EMPTY_INTERVALS;
    const raw = dateSpecificSlots
      .filter(s => s.date === selectedDate)
      .map(s => ({ startTime: s.startTime, endTime: s.endTime, scheduleType: s.scheduleType }))
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    if (raw.length <= 1) return raw;
    const merged: typeof raw = [];
    for (const r of raw) {
      const last = merged[merged.length - 1];
      if (last && timeToMinutes(last.endTime) >= timeToMinutes(r.startTime) && (last.scheduleType ?? 'standard') === (r.scheduleType ?? 'standard')) {
        if (timeToMinutes(r.endTime) > timeToMinutes(last.endTime)) {
          last.endTime = r.endTime;
        }
      } else {
        merged.push({ ...r });
      }
    }
    return merged;
  }, [dateSpecificSlots, selectedDate]);

  // Keep the ref in sync so the timer always reads the latest slotsForDate
  slotsForDateRef.current = slotsForDate;

  // Initialize ranges only when the selected date changes — not on every parent re-render.
  // When the user picks a new date, show a 1.5 s loader before auto-filling.
  useEffect(() => {
    if (initializedDateRef.current === selectedDate) return;
    initializedDateRef.current = selectedDate;
    initialFillEmptyRef.current = false;

    if (!selectedDate) {
      setRanges([]);
      setInitialRanges([]);
      return;
    }

    const timer = setTimeout(() => {
      const currentSlots = slotsForDateRef.current;
      let init: TimeRange[];
      if (currentSlots.length > 0) {
        // Load saved specific-day slots as editable ranges
        init = currentSlots.map(r => ({ ...r }));
      } else {
        // No saved slots yet — mark for late-arriving data
        init = [];
        initialFillEmptyRef.current = true;
      }
      setRanges(init);
      setInitialRanges(init.map(r => ({ ...r })));
      setDateLoading(false);
    }, 1250);

    return () => clearTimeout(timer);
  // slotsForDate is read via ref (slotsForDateRef) so the timer always gets the latest value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Late-arriving data: fill ranges when data arrives after the loading phase.
  // Covers two cases:
  //  1. Timer already fired and found no data (initialFillEmptyRef === true)
  //  2. Data arrived after loading finished but ranges are still empty
  useEffect(() => {
    if (slotsForDate.length === 0) return;
    // Case 1: timer explicitly flagged "empty" — fill now
    if (initialFillEmptyRef.current) {
      initialFillEmptyRef.current = false;
      const init = slotsForDate.map(r => ({ ...r }));
      setRanges(init);
      setInitialRanges(init.map(r => ({ ...r })));
      return;
    }
    // Case 2: loading finished, ranges still empty — data arrived between renders
    if (!dateLoading && ranges.length === 0 && initialRanges.length === 0) {
      const init = slotsForDate.map(r => ({ ...r }));
      setRanges(init);
      setInitialRanges(init.map(r => ({ ...r })));
    }
  }, [slotsForDate, dateLoading, ranges.length, initialRanges.length]);

  // Emit preview of current ranges to parent calendar whenever they change
  useEffect(() => {
    if (!onPreviewChange) return;
    if (!selectedDate) { onPreviewChange([]); return; }
    const filled = ranges.filter(r => r.startTime && r.endTime);
    onPreviewChange(
      filled.map(r => ({ date: selectedDate, startTime: r.startTime, endTime: r.endTime, scheduleType: r.scheduleType }))
    );
  }, [ranges, selectedDate, onPreviewChange]);

  const handleDateChange = useCallback(async (newDate: string) => {
    if (newDate < todayStr || newDate === selectedDate) return;
    setRanges([]);
    setInitialRanges([]);
    setDateLoading(true);
    setSelectedDate(newDate);
    initializedDateRef.current = newDate;
    initialFillEmptyRef.current = false;
    onDateChange?.(newDate);
    // Fetch the one_time_availability slots for the week of the selected date
    if (fetchSlotsForWeek) {
      try {
        const [y, m, d] = newDate.split('-').map(Number);
        const dateObj = new Date(y, (m || 1) - 1, d || 1);
        const dayOfWeek = dateObj.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const weekStart = new Date(dateObj);
        weekStart.setDate(dateObj.getDate() + mondayOffset);
        weekStart.setHours(0, 0, 0, 0);

        const allSlots = await fetchSlotsForWeek(weekStart);
        // Filter for the selected date and fill ranges
        const forDate = allSlots
          .filter(s => s.date === newDate)
          .map(s => ({ startTime: s.startTime, endTime: s.endTime, scheduleType: s.scheduleType }))
          .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
        const init: TimeRange[] = forDate.length > 0 ? forDate.map(r => ({ ...r })) : [];
        setRanges(init);
        setInitialRanges(init.map(r => ({ ...r })));
        setDateLoading(false);
        return;
      } catch {
        // Fall through to timer-based approach
      }
    }
  }, [todayStr, selectedDate, fetchSlotsForWeek]);

  const updateRange = useCallback((index: number, field: 'startTime' | 'endTime', value: string) => {
    setRanges(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      // Only sort once both times are set in all ranges
      if (next.every(r => r.startTime && r.endTime)) {
        next.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
      }
      return next;
    });
  }, []);

  const removeRange = useCallback((index: number) => {
    setRanges(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Add an empty row — user picks times manually, no auto-fill
  const addRange = useCallback(() => {
    setRanges(prev => [...prev, { startTime: '', endTime: '', scheduleType: 'standard' }]);
  }, []);

  const updateScheduleType = useCallback((index: number, value: 'standard' | 'reschedule_only') => {
    setRanges(prev => {
      const next = [...prev];
      next[index] = { ...next[index], scheduleType: value };
      return next;
    });
  }, []);

  const handleUndo = useCallback(() => {
    setRanges(initialRanges.map(r => ({ ...r })));
  }, [initialRanges]);

  // Occupied intervals: only recurring work schedules. Blocked slots are selectable
  // (user gets a confirmation modal before overriding them).
  const occupiedForDay = useMemo(() => {
    return schedulesForDay.map(s => ({ startTime: s.startTime, endTime: s.endTime }));
  }, [schedulesForDay]);

  // Check if any of the current ranges overlap with blocked slots for this date
  const overlapsWithBlocked = useMemo(() => {
    if (!selectedDate || blockedSlots.length === 0 || ranges.length === 0) return false;
    const dayBlocked = blockedSlots.filter(s => s.date === selectedDate);
    if (dayBlocked.length === 0) return false;
    for (const range of ranges) {
      if (!range.startTime || !range.endTime) continue;
      const rStart = timeToMinutes(range.startTime);
      let rEnd = timeToMinutes(range.endTime);
      if (rEnd === 0) rEnd = 1440;
      for (const b of dayBlocked) {
        const bStart = timeToMinutes(b.time);
        const bEnd = bStart + (b.duration || 30);
        if (rStart < bEnd && rEnd > bStart) return true;
      }
    }
    return false;
  }, [selectedDate, blockedSlots, ranges]);

  const errors = useMemo(
    () => ranges.map((r, i) => validateRange(r, i, ranges, occupiedForDay)),
    [ranges, occupiedForDay],
  );

  const hasChanges = useMemo(() => {
    if (ranges.length !== initialRanges.length) return true;
    const sortedCurrent = [...ranges].sort((a, b) => a.startTime.localeCompare(b.startTime));
    const sortedInitial = [...initialRanges].sort((a, b) => a.startTime.localeCompare(b.startTime));
    return sortedCurrent.some((r, i) => {
      const orig = sortedInitial[i];
      return !orig || r.startTime !== orig.startTime || r.endTime !== orig.endTime || (r.scheduleType ?? 'standard') !== (orig.scheduleType ?? 'standard');
    });
  }, [ranges, initialRanges]);

  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  const allFilled = ranges.every(r => r.startTime && r.endTime);
  const noErrors = errors.every(e => e === '');
  const canSave = !!selectedDate && !dateLoading && allFilled && noErrors && hasChanges && !saving && !loading;

  const doSave = async () => {
    setSaving(true);
    try {
      await onSave(ranges.map(r => ({ startTime: r.startTime, endTime: r.endTime, weekdayIdx, date: selectedDate, scheduleType: r.scheduleType ?? 'standard' })), selectedDate);
      onHasChangesChange?.(false);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    if (overlapsWithBlocked) {
      setShowBlockedConfirm(true);
      return;
    }
    await doSave();
  };

  const panelNode = (
    <div id="specific-day-panel"
      className={
        isMobile
          ? 'fixed bottom-0 left-0 max-h-[85vh] rounded-t-2xl z-[1300] bg-white border border-gray-200 shadow-lg flex flex-col overflow-hidden'
          : 'w-[33%] min-w-[280px] max-w-96 max-h-[37.5rem] bg-white border shadow-lg border-gray-200 rounded-xl flex flex-col overflow-hidden relative z-10'
      }
      style={isMobile ? { width: vpWidth } : undefined}
    >
      {/* Mobile drag handle */}
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
      )}
      {/* Header */}
      <div className="py-3.5 px-4 border-b border-gray-200 bg-violet-50 flex items-center justify-between">
        <h3 className="m-0 text-[0.9375rem] font-bold text-violet-700">
          Configurar horario día específico
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded cursor-pointer text-gray-500 text-base transition-colors hover:bg-gray-100"
          aria-label="Cerrar"
        >
          ×
        </button>
      </div>

      {/* Link de ayuda: "Cómo funciona tu agenda" — común a los 3 modales de configuración */}
      {onOpenAgendaHelp && (
        <div
          data-testid="agenda-help-cta-banner"
          className="shrink-0 px-4 py-2.5 bg-violet-50 border-b border-violet-100 flex items-center"
        >
          <button
            type="button"
            onClick={onOpenAgendaHelp}
            className="text-[0.75rem] text-[#6D57BC] font-medium bg-transparent border-none cursor-pointer p-0 text-left hover:underline underline-offset-2 transition-all"
          >
            ¿Querés entender cómo funciona tu agenda? →
          </button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden relative" style={{ scrollbarWidth: 'none' }}>
        {(saving || loading || dateLoading) && (
          <div className="absolute inset-0 bg-white/80 z-10 grid place-items-center">
            <CircularProgress sizeRem={2} label={dateLoading ? 'Cargando...' : 'Guardando...'} />
          </div>
        )}

        <div className="py-3 px-4">
          {/* Resumen de la semana visible — se muestra siempre, incluso antes de elegir fecha */}
          <div id="specific-day-week-summary" className="mb-3">
            <p className="mb-1.5 text-[0.8125rem] font-semibold text-gray-700 m-0">
              Días con horario específico esta semana
            </p>
            {weekSummary.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {weekSummary.map((day) => {
                  const isPast = day.date < todayStr;
                  const isSelected = day.date === selectedDate;
                  return (
                    <button
                      key={day.date}
                      type="button"
                      disabled={isPast}
                      onClick={() => handleDateChange(day.date)}
                      className={`w-full text-left py-1.5 px-2.5 text-xs rounded-lg border transition-colors ${
                        isPast
                          ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                          : isSelected
                            ? 'border-violet-700 bg-violet-50 text-violet-700 cursor-pointer'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-violet-300 hover:bg-violet-50/50 cursor-pointer'
                      }`}
                    >
                      <span className="font-semibold">{formatCalendarDay(day.date)}</span>
                      {' — '}
                      <span className={isPast ? '' : 'text-gray-500'}>
                        {day.ranges.map(r => `${r.startTime}–${r.endTime}`).join(', ')}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="m-0 text-xs text-gray-400">
                Sin días específicos configurados esta semana.
              </p>
            )}
          </div>

          {/* Date picker */}
          <div id="specific-day-date-picker" className="mb-3">
            <label className="block mb-1.5 text-[0.8125rem] font-semibold text-gray-700">
              Fecha
            </label>
            <div className="relative">
              <input
                type="date"
                ref={dateInputRef}
                value={selectedDate}
                min={todayStr}
                onChange={(e) => handleDateChange(e.target.value)}
                className="absolute inset-0 opacity-0 pointer-events-none"
                tabIndex={-1}
              />
              <button
                type="button"
                onClick={() => { try { dateInputRef.current?.showPicker(); } catch { /* noop */ } }}
                className="w-full py-2 px-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 outline-none text-left cursor-pointer hover:border-violet-700 box-border flex items-center justify-between"
              >
                <span className={selectedDate ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedDate ? formatCalendarDay(selectedDate) : 'Seleccionar fecha'}
                </span>
                <CalendarRecurrentIcon size={14} color="#9ca3af" />
              </button>
            </div>
          </div>

          {/* Time ranges */}
          <div id="specific-day-ranges" className="mb-1">
            <label className="block mb-1.5 text-[0.8125rem] font-semibold text-gray-700">
              Horarios
            </label>
            {ranges.map((range, idx) => (
              <TimeRangeRow
                key={`range-${idx}`}
                range={range}
                index={idx}
                allRanges={ranges}
                canRemove={true}
                error={errors[idx] || ''}
                onChange={(i, field, value) => updateRange(i, field, value)}
                onRemove={(i) => removeRange(i)}
                schedulesForDay={occupiedForDay}
                onScheduleTypeChange={updateScheduleType}
              />
            ))}
            <button
              type="button"
              onClick={addRange}
              className="inline-flex items-center gap-1 py-1 px-2 text-xs font-semibold text-violet-700 bg-transparent border-none rounded cursor-pointer transition-colors mt-0.5 hover:bg-violet-50"
            >
              + Agregar horario
            </button>
          </div>
        </div>
        {/* Mock reschedule entry for onboarding tour */}
        {mockRescheduleEntry && (
          <div id="onboarding-panel-day-row" className="py-2.5 px-4 border-b border-amber-200 bg-amber-50/50">
            <div id="onboarding-panel-day-header" className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked readOnly className="w-4 h-4 accent-violet-700 pointer-events-none" tabIndex={-1} />
              <span className="text-[0.8125rem] font-semibold text-gray-900 flex-1">Ejemplo</span>
              <button type="button" tabIndex={-1} className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded text-violet-500 pointer-events-none">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5.5" y="1.5" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10.5 13V13.5C10.5 14.3284 9.82843 15 9 15H3.5C2.67157 15 2 14.3284 2 13.5V5.5C2 4.67157 2.67157 4 3.5 4H4" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
            </div>
            <div className="pl-6">
              <div className="mb-1">
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="w-full py-1 px-2 bg-gray-50 border border-gray-200 rounded text-[0.75rem] text-gray-500">09:00</div>
                  </div>
                  <span className="text-gray-400 text-xs shrink-0">—</span>
                  <div className="flex-1 min-w-0">
                    <div className="w-full py-1 px-2 bg-gray-50 border border-gray-200 rounded text-[0.75rem] text-gray-500">12:00</div>
                  </div>
                  <span id="onboarding-reschedule-mock-row" className="shrink-0 px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold border bg-amber-50 text-amber-700 border-amber-300">
                    Repr.
                  </span>
                  <button type="button" tabIndex={-1} className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded text-sm shrink-0 text-red-500 pointer-events-none" aria-label="Eliminar rango">
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!saving && !loading && !dateLoading && (
        <div id="specific-day-footer" className="py-3 px-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            style={canSave ? { background: '#6D57BC' } : { background: '#9ca3af', opacity: 0.6 }}
            className={`w-full py-2.5 px-4 text-sm font-semibold text-white border-none rounded-lg transition-colors ${
              canSave
                ? 'cursor-pointer hover:brightness-90'
                : 'cursor-not-allowed'
            }`}
          >
            Guardar cambios
          </button>
          {hasChanges && (
            <button
              type="button"
              onClick={handleUndo}
              className="w-full mt-1.5 py-2 px-4 text-sm font-semibold text-gray-500 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Deshacer
            </button>
          )}
          {!hasChanges && (
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-1.5 py-2 px-4 text-sm font-semibold text-gray-500 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Cancelar
            </button>
          )}
        </div>
      )}
      {showBlockedConfirm && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowBlockedConfirm(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 12, width: '24rem', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: '1.5rem' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
              Horario bloqueado
            </h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
              El horario que estás configurando se solapa con un bloqueo existente. Si continuás, se eliminará el bloqueo en ese rango.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowBlockedConfirm(false)}
                className="py-2 px-4 text-sm font-semibold text-gray-500 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => { setShowBlockedConfirm(false); await doSave(); }}
                style={{ background: '#6D57BC' }}
                className="py-2 px-4 text-sm font-semibold text-white border-none rounded-lg cursor-pointer transition-colors hover:brightness-90"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <>
        <div className="fixed inset-0 bg-black/40 z-[1299]" onClick={onClose} />
        {panelNode}
      </>,
      document.body,
    );
  }

  return panelNode;
};

export default SpecificDayPanel;
